import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";
import { FoodsIcon, FoodsToggleButton, FoodsToggleButtonType, FoodsTooltip, Typography } from "@foodpilot/foods";
import type { PermissionScopeValue } from "../permissionScope";

export type PermissionScopeSelectorProps = {
    browsedScope: PermissionScopeValue | null;
    committedScope: PermissionScopeValue | null;
    orgCount: number;
    platformCount: number;
    onBrowseScope: (scope: PermissionScopeValue) => void;
};

const ScopeCountBadge = (props: { count: number; active: boolean }) => {
    const { count, active } = props;

    return (
        <Box
            component="span"
            sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: theme.spacing(2.75),
                height: theme.spacing(2.5),
                padding: theme.spacing(0, 0.75),
                borderRadius: theme.spacing(1.25),
                backgroundColor: active ? theme.custom.grey[100] : theme.custom.grey[400],
            })}
        >
            <Typography
                variant="small-bold"
                component="span"
                sx={(theme) => ({
                    lineHeight: 1,
                    color: active ? theme.palette.primary[700] : theme.custom.grey[2500],
                })}
            >
                {count}
            </Typography>
        </Box>
    );
};

export const PermissionScopeSelector = (props: PermissionScopeSelectorProps) => {
    const { browsedScope, committedScope, orgCount, platformCount, onBrowseScope } = props;
    const { t } = useTranslation();

    const scopeLabels: Record<PermissionScopeValue, string> = {
        org: t("settings.roles.scope.organization", "Organization"),
        platform: t("settings.roles.scope.platform", "Platform"),
    };

    const buildScopeButton = (scope: PermissionScopeValue): FoodsToggleButtonType => {
        const disabled = committedScope !== null && committedScope !== scope;

        return {
            id: scope,
            label: scopeLabels[scope],
            startIcon: disabled ? "lock" : undefined,
            disabled,
            onClick: () => onBrowseScope(scope),
        };
    };

    const buttons: FoodsToggleButtonType[] = [buildScopeButton("org"), buildScopeButton("platform")];

    const selectedScope = committedScope ?? browsedScope;
    const selectedButton = buttons.find((button) => button.id === selectedScope);
    const isLocked = committedScope !== null;

    const hint =
        committedScope !== null ?
            t("settings.roles.scope.hint_committed", "Scope: {{scope}}, set by the selected permissions.", {
                scope: scopeLabels[committedScope],
            })
        :   t("settings.roles.scope.hint_free", "The first selected permission sets the role scope.");

    const lockedTooltip = t("settings.roles.scope.locked_tooltip", "Clear all permissions to change the scope.");

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                <Typography
                    variant="h6"
                    id="permission-scope-label"
                    sx={(theme) => ({ color: theme.custom.grey[2800] })}
                >
                    {t("settings.roles.scope.label", "Scope")}
                </Typography>
                <Stack
                    role="radiogroup"
                    aria-labelledby="permission-scope-label"
                    direction="row"
                    alignItems="center"
                    spacing={2}
                >
                    <FoodsTooltip title={isLocked ? lockedTooltip : ""} disableHoverListener={!isLocked}>
                        <Box component="span" sx={{ display: "inline-flex" }}>
                            <FoodsToggleButton buttons={buttons} initialSelected={selectedButton} buttonGroup={true} />
                        </Box>
                    </FoodsTooltip>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body-medium" sx={(theme) => ({ color: theme.custom.grey[2500] })}>
                                {scopeLabels.org}
                            </Typography>
                            <ScopeCountBadge count={orgCount} active={selectedScope === "org"} />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body-medium" sx={(theme) => ({ color: theme.custom.grey[2500] })}>
                                {scopeLabels.platform}
                            </Typography>
                            <ScopeCountBadge count={platformCount} active={selectedScope === "platform"} />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            <FoodsTooltip title={isLocked ? lockedTooltip : ""} disableHoverListener={!isLocked}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    {isLocked && (
                        <Box
                            component="span"
                            sx={(theme) => ({ color: theme.custom.grey[2500], display: "inline-flex" })}
                        >
                            <FoodsIcon icon="lock" size={1.25} />
                        </Box>
                    )}
                    <Typography
                        variant="body-medium"
                        role="status"
                        aria-live="polite"
                        sx={(theme) => ({ color: theme.custom.grey[2500] })}
                    >
                        {hint}
                    </Typography>
                </Stack>
            </FoodsTooltip>
        </Stack>
    );
};
